import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { trailersTableColumns } from "../../../data/FormColumns/Fleet/trailersTableColumns";
import "./TrailersTable.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchTrailers = async (
  token: string | null | undefined,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrailers`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { areActive },
  });
  return data;
};

const TrailersTable = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get("page") || "0", 10);
  const [areActive, setAreActive] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: trailersTable,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["trailersTable"],
    queryFn: () => fetchTrailers(token, areActive),
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    const trailerConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/trailerHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    trailerConnection.on("TrailerUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["trailersTable"] });
    });

    trailerConnection.start();

    return () => {
      trailerConnection.stop();
    };
  }, [token, queryClient]);

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [areActive, token, refetch]);

  const onRowClick = (params: any, event: React.MouseEvent) => {
    const url = `trailer/${params.row.id}`;

    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();

      window.open("assets/" + url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: newPage.toString() });
  };

  const filteredTrailers = useMemo(() => {
    if (!trailersTable) return [];
    return trailersTable.filter((trailer: any) =>
      [trailer.unitNumber, trailer.vin, trailer.licencePlate].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [trailersTable, searchTerm]);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  return (
    <Box className="trailers-table" sx={{ height: 900 }}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={areActive}
              onChange={handleSwitchChange}
              color="primary"
            />
          }
          label={`${areActive ? "Active" : "Inactive"} Trailers`}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Unit Number, VIN, Licence Plate"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="trailers"
          rows={filteredTrailers}
          columns={trailersTableColumns}
          compactViewConfig={{
            primaryField: "unitNumber",
            secondaryFields: ["make", "model"],
            detailFields: ["vin", "year", "trailerType", "transactionType"],
            actionFields: ["delete"],
            statusField: "status",
          }}
          pageSizeOptions={[10]}
          paginationModel={{ page: pageFromUrl, pageSize: 10 }}
          paginationMode="client"
          onPaginationModelChange={(m) => handlePageChange(m.page)}
          onRowClick={onRowClick}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default TrailersTable;
